import React, { useState, useMemo } from "react";
import { Calculator } from "lucide-react";
import { useI18n } from "../../i18n/I18nContext";

// Module-scope field so the input identity is stable across renders
const Field = ({ label, value, setValue, min, max, step, prefix = "", suffix = "" }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="text-sm font-600 text-[#0a2540]">{label}</label>
      <span className="text-sm font-700 text-[#0a6ed1]">
        {prefix}
        {value.toLocaleString()}
        {suffix}
      </span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => setValue(Number(e.target.value))}
      className="w-full accent-[#0a2540] cursor-pointer"
    />
  </div>
);

const Metric = ({ value, label, tone }) => (
  <div>
    <div className={`font-display font-800 text-3xl ${tone || ""}`}>{value}</div>
    <div className="text-sm text-slate-300 mt-1">{label}</div>
  </div>
);

// Interactive Marketing ROI calculator (front-end only)
const RoiCalculator = () => {
  const { t } = useI18n();
  const [budget, setBudget] = useState(20000);
  const [cvr, setCvr] = useState(2.5);
  const [aov, setAov] = useState(250);
  const [cpc, setCpc] = useState(1.8);

  const result = useMemo(() => {
    const clicks = cpc > 0 ? budget / cpc : 0;
    const customers = clicks * (cvr / 100);
    const revenue = customers * aov;
    const roi = budget > 0 ? ((revenue - budget) / budget) * 100 : 0;
    return {
      clicks: Math.round(clicks),
      customers: Math.round(customers),
      revenue: Math.round(revenue),
      roi: Math.round(roi),
    };
  }, [budget, cvr, aov, cpc]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 bg-white rounded-2xl border border-slate-200 p-8">
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#0a2540]">
            <Calculator className="w-5 h-5 text-white" />
          </span>
          <div>
            <h3 className="font-display font-700 text-xl text-[#0a2540]">
              {t("roiTitle")}
            </h3>
            <p className="text-sm text-slate-500">{t("roiSubtitle")}</p>
          </div>
        </div>
        <Field label={t("roiBudget")} value={budget} setValue={setBudget} min={1000} max={200000} step={1000} prefix="$" />
        <Field label={t("roiCpc")} value={cpc} setValue={setCpc} min={0.2} max={10} step={0.1} prefix="$" />
        <Field label={t("roiCvr")} value={cvr} setValue={setCvr} min={0.1} max={15} step={0.1} suffix="%" />
        <Field label={t("roiAov")} value={aov} setValue={setAov} min={20} max={2000} step={10} prefix="$" />
      </div>

      <div className="bg-[#0a2540] rounded-xl p-8 text-white flex flex-col justify-center">
        <span className="text-sm font-600 text-[#5eb0f5] uppercase tracking-wider">
          {t("roiProjected")}
        </span>
        <div className="mt-6 grid grid-cols-2 gap-6">
          <Metric value={result.clicks.toLocaleString()} label={t("roiClicks")} />
          <Metric value={result.customers.toLocaleString()} label={t("roiCustomers")} />
          <Metric value={`$${result.revenue.toLocaleString()}`} label={t("roiRevenue")} />
          <Metric
            value={`${result.roi}%`}
            label={t("roiRoi")}
            tone={result.roi >= 0 ? "text-[#5eb0f5]" : "text-red-300"}
          />
        </div>
      </div>
    </div>
  );
};

export default RoiCalculator;
